import type { AppSelector } from 'app/store'
import getDataSelector from 'core/utils/getDataSelector'
import { createSharedSelector } from 'core/utils/selectorHelpers'
import {
  formatGPUSizesToString,
  getGPUProfiles,
} from 'k8s/components/cluster/deployment/form-components/helpers'
import DataKeys from 'k8s/DataKeys'
import { kaapiConfigMapsSelector } from '../config-maps/selectors'
import { kaapiEventsSelector } from '../events/selectors'
import { kaapiMachineDeploymentsSelector } from '../machine-deployment/selectors'
import { kaapiOpenStackServersSelector } from '../openstack-servers/selectors'
import {
  kaapiNodesSelector,
  kaapiNodesSelectorForAllClusters,
} from '../workload-clusters/nodes/selectors'
import { getMachineHealth } from './machineHealth'

export enum GPUPartitioningStrategy {
  Passthrough = 'Passthrough',
  MIG = 'MIG',
  Timeslice = 'Time-sliced',
}

const isMasterMachine = (machine) =>
  !!machine?.metadata?.labels?.['cluster.x-k8s.io/control-plane-name']

// Common function to process kaapi machines data for cluster specific nodes or all clusters
export const processMachinesData = (
  kaapiMachines,
  kaapiClusters,
  machineDeployments,
  kaapiNodes,
  kaapiConfigMaps,
  openStackServers,
  kaapiEvents?: any[],
  byoHosts?: any[],
  byoMachines?: any[],
) => {
  return kaapiMachines
    .map((kaapiMachine) => {
      // Find the openstack machine template for the machine deployment
      const machineDeployment = machineDeployments?.find((md) => {
        return (
          md?.metadata?.name === kaapiMachine?.metadata?.labels['cluster.x-k8s.io/deployment-name']
        )
      })

      const isBYOMachine = kaapiMachine?.spec?.infrastructureRef?.kind === 'ByoMachine'

      // The ByoHost carries the agent-reported install/join failure. It is linked
      // by status.machineRef rather than by name - the ByoHost keeps its own
      // registered host name, which has no relation to the Machine's.
      const byoHost = isBYOMachine
        ? byoHosts?.find((h) => h?.status?.machineRef?.name === kaapiMachine?.metadata?.name)
        : undefined

      // The ByoMachine carries the "no host to claim" failure. Linked by
      // spec.infrastructureRef.name - the authoritative Machine -> ByoMachine
      // link CAPI itself uses - even though today that name happens to equal
      // the Machine's own name.
      const byoMachine = isBYOMachine
        ? byoMachines?.find(
            (bm) => bm?.metadata?.name === kaapiMachine?.spec?.infrastructureRef?.name,
          )
        : undefined

      // Get the physical node for the machine. status.nodeRef is only set once
      // the node has joined the workload cluster, so while a BYOH machine is
      // still provisioning, fall back to the ByoHost's own registered name -
      // known from onboarding, well before nodeRef gets populated.
      const physicalNode = kaapiMachine?.status?.nodeRef?.name || byoHost?.metadata?.name

      // Find the k8s node for the machine
      const k8sNode = kaapiNodes?.find((node) => {
        return (
          node?.metadata?.name === physicalNode ||
          node?.metadata?.name === kaapiMachine?.metadata?.name
        )
      })

      const k8sNodeStatus = k8sNode?.status?.conditions
        ? (k8sNode?.status?.conditions?.find((c) => c.status === 'True') ?? { type: 'NotReady' })
        : 'Unknown'

      // GPU Details
      const gpuModel = k8sNode?.metadata?.labels?.['nvidia.com/gpu.product'] || ''
      const migConfig = k8sNode?.metadata?.labels?.['nvidia.com/mig.config'] || ''
      const gpuMemory = k8sNode?.metadata?.labels?.['nvidia.com/gpu.memory'] || ''

      // Find the matching MIG profile from config maps
      let gpuMigProfile = ''
      if (gpuModel && migConfig) {
        const { gpuSizes } = getGPUProfiles(kaapiConfigMaps, gpuModel, gpuMemory)
        gpuMigProfile = gpuSizes?.[migConfig] ? formatGPUSizesToString(gpuSizes?.[migConfig]) : ''
      }
      const gpuEnabled = gpuModel ? true : false
      const gpuPartitioningStrategy = gpuEnabled
        ? migConfig !== 'all-disabled' &&
          k8sNode?.metadata?.labels?.['nvidia.com/gpu.deploy.mig-manager'] === 'true'
          ? GPUPartitioningStrategy.MIG
          : k8sNode?.metadata?.labels?.['nvidia.com/gpu.sharing-strategy'] === 'time-slicing'
            ? GPUPartitioningStrategy.Timeslice
            : GPUPartitioningStrategy.Passthrough
        : ''
      const gpuMachine = k8sNode?.metadata?.labels?.['nvidia.com/gpu.machine'] || ''
      const gpuReplicas = k8sNode?.metadata?.labels?.['nvidia.com/gpu.replicas'] || ''
      const gpuCount = k8sNode?.metadata?.labels?.['nvidia.com/gpu.count'] || ''
      const openStackServer = openStackServers?.find(
        (s) => s?.metadata?.name === kaapiMachine?.metadata?.name,
      )
      const openStackServerEvent = kaapiEvents
        ?.filter(
          (e) =>
            e?.type === 'Warning' &&
            e?.involvedObject?.kind === 'OpenStackServer' &&
            e?.involvedObject?.name === kaapiMachine?.metadata?.name,
        )
        ?.sort(
          (a, b) => Date.parse(b?.lastTimestamp || '0') - Date.parse(a?.lastTimestamp || '0'),
        )?.[0]
      const cluster = kaapiClusters?.find(
        (c) => c?.metadata?.name === kaapiMachine?.spec?.clusterName,
      )

      const machineHealth = getMachineHealth(
        kaapiMachine,
        undefined,
        openStackServer,
        openStackServerEvent,
        cluster,
        byoHost,
        byoMachine,
      )
      return {
        ...kaapiMachine,
        nodeType: gpuEnabled ? 'GPU' : isMasterMachine(kaapiMachine) ? 'Control Plane' : 'Worker',
        name: kaapiMachine?.metadata?.name,
        clusterName: kaapiMachine?.spec?.clusterName,
        phase: kaapiMachine?.status?.phase,
        machineHealth,
        // The error modal deep-links to the VM with this. spec.providerID is
        // only set once the machine is provisioned, so on a VM that failed
        // during create the server CR is the only place the ID exists - which
        // is precisely when the operator needs the link.
        instanceId: openStackServer?.status?.instanceID,
        k8sNodeStatus,
        k8sNodeStatusType: k8sNodeStatus?.type,
        isBYOMachine,
        unschedulable: k8sNode?.spec?.unschedulable,
        uid: kaapiMachine?.metadata?.uid,
        machineDeployment,
        cpuUsage: { current: k8sNode?.usedCpu?.toFixed(2), total: k8sNode?.maxCpu?.toFixed(2) },
        memoryUsage: {
          current: k8sNode?.usedMemory?.toFixed(2),
          total: k8sNode?.maxMemory?.toFixed(2),
        },
        storageUsage: { current: 5.5, total: 40 },
        physicalNode,
        gpu: {
          enabled: gpuEnabled,
          model: gpuModel,
          partitioningStrategy: gpuPartitioningStrategy,
          details: gpuMigProfile ? `${gpuMachine} | ${gpuMigProfile}` : gpuMachine,
          memory: gpuMemory,
          replicas: gpuReplicas,
          count: gpuCount,
          migConfig: migConfig,
        },
      }
    })
    ?.sort((a, b) => (a.nodeType === 'Control Plane' ? -1 : b.nodeType === 'Control Plane' ? 1 : 0))
}

export const kaapiMachinesSelector: AppSelector<any[]> = createSharedSelector(
  getDataSelector<DataKeys.KaapiMachines>(DataKeys.KaapiMachines),
  getDataSelector<DataKeys.KaapiClusters>(DataKeys.KaapiClusters),
  kaapiMachineDeploymentsSelector,
  kaapiNodesSelector,
  kaapiConfigMapsSelector,
  kaapiOpenStackServersSelector,
  kaapiEventsSelector,
  getDataSelector<DataKeys.KaapiBYOHosts>(DataKeys.KaapiBYOHosts),
  getDataSelector<DataKeys.KaapiBYOMachines>(DataKeys.KaapiBYOMachines),
  (
    kaapiMachines,
    kaapiClusters,
    machineDeployments,
    kaapiNodes,
    kaapiConfigMaps,
    openStackServers,
    kaapiEvents,
    byoHosts,
    byoMachines,
  ): any[] =>
    processMachinesData(
      kaapiMachines,
      kaapiClusters,
      machineDeployments,
      kaapiNodes,
      kaapiConfigMaps,
      openStackServers,
      kaapiEvents,
      byoHosts,
      byoMachines,
    ),
)

export const kaapiMachinesSelectorAllClusters: AppSelector<any[]> = createSharedSelector(
  getDataSelector<DataKeys.KaapiMachines>(DataKeys.KaapiMachines),
  getDataSelector<DataKeys.KaapiClusters>(DataKeys.KaapiClusters),
  kaapiMachineDeploymentsSelector,
  kaapiNodesSelectorForAllClusters,
  kaapiConfigMapsSelector,
  kaapiOpenStackServersSelector,
  kaapiEventsSelector,
  getDataSelector<DataKeys.KaapiBYOHosts>(DataKeys.KaapiBYOHosts),
  getDataSelector<DataKeys.KaapiBYOMachines>(DataKeys.KaapiBYOMachines),
  (
    kaapiMachines,
    kaapiClusters,
    machineDeployments,
    kaapiNodes,
    kaapiConfigMaps,
    openStackServers,
    kaapiEvents,
    byoHosts,
    byoMachines,
  ): any[] =>
    processMachinesData(
      kaapiMachines,
      kaapiClusters,
      machineDeployments,
      kaapiNodes,
      kaapiConfigMaps,
      openStackServers,
      kaapiEvents,
      byoHosts,
      byoMachines,
    ),
)

export const kaapiMachinesForClusterSelector = ({ clusterName }): AppSelector<any[]> =>
  createSharedSelector(kaapiMachinesSelector, (kaapiMachinesSelector): any[] => {
    return kaapiMachinesSelector.filter((md) => md.spec.clusterName === clusterName)
  })
